/**
 * Tab time controls — family compatibility engine.
 *
 * Groups a tab's visualization widgets into two time "families":
 *   - trend        → line charts (time-series)
 *   - pointInTime  → bar / table / crosstab / scorecard / donut charts (point-in-time)
 */
import {
  sortGuardrailRollingOptions,
  type GuardrailRollingOption,
  type TimeSeriesViewerOptions,
  type PointInTimeViewerOptions,
  type DateGuardrails,
} from "@models/Dataset";
import type { WidgetResponse } from "@models/Widget";
import { parseWidgetConfigJson } from "@utils/widget-config";
import {
  filterTimeSeriesViewerOptions,
  filterPointInTimeViewerOptions,
} from "@utils/viewer-time-guardrails";

export type TabTimeFamilyKind = "trend" | "pointInTime";

/** Imperative family-wide handlers supplied by the dashboard page. */
export interface TabTimeControlsHandlers {
  onFamilyTimeUnitChange: (
    widgetIds: string[],
    unit: string,
    defaultRA: GuardrailRollingOption,
  ) => void;
  onFamilyTimePeriodChange: (
    widgetIds: string[],
    period: string,
    defaultRA: GuardrailRollingOption,
  ) => void;
  onFamilyRollingAverageChange: (
    widgetIds: string[],
    ra: GuardrailRollingOption,
  ) => void;
  onFamilyReset: (widgetIds: string[]) => void;
}

export const TREND_CHART_TYPES = new Set(["line"]);
export const POINT_IN_TIME_CHART_TYPES = new Set([
  "bar",
  "grouped_bar",
  "stacked_bar",
  "table",
  "crosstab",
  "scorecard",
  "donut",
]);

/** The subset of a parsed widget config the time-controls engine reads. */
interface ParsedTimeConfig {
  chartType?: string;
  title?: string;
  questionName?: string;
  timeUnit?: string;
  timePeriodSelection?: { timePeriod?: string };
  rollingAverage?: { enabled?: boolean; windowSize?: number };
  viewerOptions?: {
    enabled?: boolean;
    chartUnitAdditionalRollingAverages?: GuardrailRollingOption[];
    additionalUnits?: Record<string, unknown>;
    chartPeriodAdditionalRollingAverages?: GuardrailRollingOption[];
    additionalPeriods?: Record<string, unknown>;
  };
}

export interface TabTimeFamilyMember {
  widgetId: string;
  chartType: string;
  /** Best-effort chart title, used to name conflicting charts in Mixed helper text. */
  title: string | null;
  datasetId: string | null;
  viewerEnabled: boolean;
  /** Researcher-saved default: time unit (trend) or named period (point-in-time). */
  savedDefault: string;
  savedRollingAverage: GuardrailRollingOption;
  /** Full viewer-time contract (the selectable options the researcher exposed). */
  viewerOptions: TimeSeriesViewerOptions | PointInTimeViewerOptions | null;
}

/** A visualization named in a Mixed-configuration explanation. */
export interface TimeFamilyConflict {
  widgetId: string;
  /** null when the chart has no title — the UI shows a neutral fallback. */
  title: string | null;
}

export type FamilyCompatibility =
  | {
      status: "enabled";
      effectiveDefault: string;
      effectiveRollingAverage: GuardrailRollingOption;
      /** Whether the shared contract actually turns viewer selection on. */
      viewerEnabled: boolean;
    }
  | {
      status: "mixed";
      /** The "why", without chart names — names are rendered separately. */
      reason: string;
      /** The conflicting charts, so the UI can list them (truncated + tooltip). */
      conflicts: TimeFamilyConflict[];
    };

export interface TabTimeFamilyAnalysis {
  kind: TabTimeFamilyKind;
  count: number;
  members: TabTimeFamilyMember[];
  compatibility: FamilyCompatibility;
}

export interface TabTimeControlsAnalysis {
  /** null when the tab has fewer than 2 trend charts. */
  trend: TabTimeFamilyAnalysis | null;
  /** null when the tab has fewer than 2 point-in-time charts. */
  pointInTime: TabTimeFamilyAnalysis | null;
  /** True when either family has ≥2 members (drives trigger visibility). */
  showTrigger: boolean;
}

/** Deterministic stringify with sorted keys — order-independent contract equality. */
function stableStringify(value: unknown): string {
  if (value === null || typeof value !== "object") return JSON.stringify(value);
  if (Array.isArray(value)) return `[${value.map(stableStringify).join(",")}]`;
  const record = value as Record<string, unknown>;
  const keys = Object.keys(record).sort();
  return `{${keys
    .map((k) => `${JSON.stringify(k)}:${stableStringify(record[k])}`)
    .join(",")}}`;
}

function allSame<T>(items: T[], key: (item: T) => string): boolean {
  if (items.length === 0) return true;
  const first = key(items[0]);
  return items.every((item) => key(item) === first);
}

function buildMember(
  widget: WidgetResponse,
  config: ParsedTimeConfig,
  kind: TabTimeFamilyKind,
): TabTimeFamilyMember {
  const rollingAverage = config.rollingAverage;
  const savedRollingAverage = (
    rollingAverage?.enabled ? (rollingAverage.windowSize ?? 0) : 0
  ) as GuardrailRollingOption;

  const viewerOptions = (config.viewerOptions ?? null) as
    | TimeSeriesViewerOptions
    | PointInTimeViewerOptions
    | null;
  const viewerEnabled = viewerOptions?.enabled === true;

  return {
    widgetId: widget.id,
    chartType: String(config.chartType),
    title: config.title ?? config.questionName ?? null,
    datasetId: widget.datasetId ?? null,
    viewerEnabled,
    savedRollingAverage,
    viewerOptions,
    savedDefault:
      kind === "trend"
        ? (config.timeUnit ?? "quarter")
        : (config.timePeriodSelection?.timePeriod ?? "all_time"),
  };
}

/** A per-unit/per-period viewer config: a set of allowed RAs plus a default. */
interface AllowedRollingConfig {
  allowedRollingAverages: GuardrailRollingOption[];
  defaultRollingAverage: GuardrailRollingOption;
}

/**
 * Sort the allowed-RA set in each per-unit/per-period config so contract keys
 * are independent of the order values were appended in. The RA sets are built
 * by append/remove in the editor, so two semantically identical contracts can
 * otherwise differ only by array ordering.
 */
function normalizeRollingConfigs(
  configs: Partial<Record<string, AllowedRollingConfig>> | undefined,
): Record<string, AllowedRollingConfig> {
  const normalized: Record<string, AllowedRollingConfig> = {};
  for (const [key, config] of Object.entries(configs ?? {})) {
    if (!config) continue;
    normalized[key] = {
      allowedRollingAverages: sortGuardrailRollingOptions(
        config.allowedRollingAverages,
      ),
      defaultRollingAverage: config.defaultRollingAverage,
    };
  }
  return normalized;
}

/**
 * Order-independent key of a contract's selectable options (excludes the
 * enabled flag). Two contracts are equivalent iff their keys match. Exported so
 * the editor can compare an in-progress edit against a shared config.
 */
export function viewerContractKey(
  viewerOptions: TimeSeriesViewerOptions | PointInTimeViewerOptions | null,
  kind: TabTimeFamilyKind,
): string {
  if (kind === "trend") {
    const ts = viewerOptions as TimeSeriesViewerOptions | null;
    return stableStringify({
      chartUnitAdditionalRollingAverages: sortGuardrailRollingOptions(
        ts?.chartUnitAdditionalRollingAverages ?? [],
      ),
      additionalUnits: normalizeRollingConfigs(ts?.additionalUnits),
    });
  }
  const pit = viewerOptions as PointInTimeViewerOptions | null;
  return stableStringify({
    chartPeriodAdditionalRollingAverages: sortGuardrailRollingOptions(
      pit?.chartPeriodAdditionalRollingAverages ?? [],
    ),
    additionalPeriods: normalizeRollingConfigs(pit?.additionalPeriods),
  });
}

/** The selectable-options portion of a member's contract. */
function contractKey(
  member: TabTimeFamilyMember,
  kind: TabTimeFamilyKind,
): string {
  return viewerContractKey(member.viewerOptions, kind);
}

/** Members whose key differs from the first (family baseline) member. */
function differing(
  members: TabTimeFamilyMember[],
  keyFn: (m: TabTimeFamilyMember) => string,
): TabTimeFamilyMember[] {
  const firstKey = keyFn(members[0]);
  return members.filter((m) => keyFn(m) !== firstKey);
}

/** Structured conflict list from the culprit members (names rendered by the UI). */
function toConflicts(members: TabTimeFamilyMember[]): TimeFamilyConflict[] {
  return members.map((m) => ({ widgetId: m.widgetId, title: m.title }));
}

/** The single tab-time configuration a set of family members agrees on. */
export interface SharedTimeConfig {
  savedDefault: string;
  savedRollingAverage: GuardrailRollingOption;
  viewerOptions: TimeSeriesViewerOptions | PointInTimeViewerOptions | null;
}

/**
 * THE definition of "these family members share one tab-time configuration."
 * Returns that shared config when every member has Viewer Time Options enabled,
 * the same dataset, the same researcher-saved default + rolling average, and an
 * identical viewer-time contract — otherwise null. Never intersects partial
 * matches. This is the single source of truth reused by the popover's
 * compatibility check and the editor's alignment suggestion — do not duplicate
 * the comparison elsewhere.
 */
export function getSharedTimeConfig(
  members: TabTimeFamilyMember[],
  kind: TabTimeFamilyKind,
): SharedTimeConfig | null {
  if (members.length === 0) return null;
  if (!members.every((m) => m.viewerEnabled)) return null;
  if (!allSame(members, (m) => m.datasetId ?? "")) return null;
  if (!allSame(members, (m) => `${m.savedDefault}|${m.savedRollingAverage}`)) {
    return null;
  }
  if (!allSame(members, (m) => contractKey(m, kind))) return null;
  const first = members[0];
  return {
    savedDefault: first.savedDefault,
    savedRollingAverage: first.savedRollingAverage,
    viewerOptions: first.viewerOptions,
  };
}

function evaluateFamily(
  members: TabTimeFamilyMember[],
  kind: TabTimeFamilyKind,
): FamilyCompatibility {
  // Every visualization must have Viewer Time Options enabled — any disabled → Mixed.
  if (!members.every((m) => m.viewerEnabled)) {
    return {
      status: "mixed",
      reason: "Viewer time options are turned off on:",
      conflicts: toConflicts(members.filter((m) => !m.viewerEnabled)),
    };
  }
  if (!allSame(members, (m) => m.datasetId ?? "")) {
    return {
      status: "mixed",
      reason: "Charts are built on different datasets:",
      conflicts: toConflicts(differing(members, (m) => m.datasetId ?? "")),
    };
  }
  if (!allSame(members, (m) => `${m.savedDefault}|${m.savedRollingAverage}`)) {
    return {
      status: "mixed",
      reason: "Charts have different saved time defaults:",
      conflicts: toConflicts(
        differing(members, (m) => `${m.savedDefault}|${m.savedRollingAverage}`),
      ),
    };
  }
  if (!allSame(members, (m) => contractKey(m, kind))) {
    return {
      status: "mixed",
      reason: "Charts expose different viewer time options:",
      conflicts: toConflicts(differing(members, (m) => contractKey(m, kind))),
    };
  }

  // All mismatch checks passed → the members share one config (same source of
  // truth the editor's alignment suggestion uses).
  const shared = getSharedTimeConfig(members, kind);
  if (!shared) {
    return {
      status: "mixed",
      reason: "Charts have incompatible time configurations.",
      conflicts: [],
    };
  }
  return {
    status: "enabled",
    effectiveDefault: shared.savedDefault,
    effectiveRollingAverage: shared.savedRollingAverage,
    viewerEnabled: true,
  };
}

function buildFamily(
  kind: TabTimeFamilyKind,
  members: TabTimeFamilyMember[],
): TabTimeFamilyAnalysis | null {
  if (members.length < 2) return null;
  return {
    kind,
    count: members.length,
    members,
    compatibility: evaluateFamily(members, kind),
  };
}

/**
 * Reduce a member's saved contract to what its dataset's live guardrails allow
 * (same filtering the per-widget popover applies) so compatibility and options
 * ignore PRD-37 "compatibility-only" states. No-op until guardrails are known.
 */
function applyGuardrails(
  member: TabTimeFamilyMember,
  kind: TabTimeFamilyKind,
  guardrailsByDataset?: Record<string, DateGuardrails>,
): TabTimeFamilyMember {
  if (!guardrailsByDataset || !member.datasetId || !member.viewerOptions) {
    return member;
  }
  const guardrails = guardrailsByDataset[member.datasetId];
  if (!guardrails) return member;
  const viewerOptions =
    kind === "trend"
      ? filterTimeSeriesViewerOptions(
          member.viewerOptions as TimeSeriesViewerOptions,
          member.savedDefault,
          guardrails,
        )
      : filterPointInTimeViewerOptions(
          member.viewerOptions as PointInTimeViewerOptions,
          member.savedDefault,
          guardrails,
        );
  return { ...member, viewerOptions };
}

/**
 * Group a tab's widgets into trend / point-in-time family members (ungated by
 * count). When `guardrailsByDataset` is supplied, each member's contract is
 * filtered against its dataset's live guardrails so the result matches what the
 * per-widget viewer controls actually offer.
 */
export function buildFamilyMembers(
  widgets: WidgetResponse[] | undefined,
  guardrailsByDataset?: Record<string, DateGuardrails>,
): Record<TabTimeFamilyKind, TabTimeFamilyMember[]> {
  const trend: TabTimeFamilyMember[] = [];
  const pointInTime: TabTimeFamilyMember[] = [];

  for (const widget of widgets ?? []) {
    if (widget.widgetType !== "VISUALIZATION") continue;
    const config = parseWidgetConfigJson(
      widget.configJson,
      "VISUALIZATION",
      widget.id,
    ) as ParsedTimeConfig | null;
    if (!config) continue;
    const chartType = String(config.chartType);
    if (TREND_CHART_TYPES.has(chartType)) {
      trend.push(
        applyGuardrails(
          buildMember(widget, config, "trend"),
          "trend",
          guardrailsByDataset,
        ),
      );
    } else if (POINT_IN_TIME_CHART_TYPES.has(chartType)) {
      pointInTime.push(
        applyGuardrails(
          buildMember(widget, config, "pointInTime"),
          "pointInTime",
          guardrailsByDataset,
        ),
      );
    }
  }

  return { trend, pointInTime };
}

/**
 * Analyze a tab's widgets into trend / point-in-time time-control families.
 * When `guardrailsByDataset` is supplied, each member's contract is first
 * filtered against its dataset's live guardrails so the analysis matches what
 * the per-widget viewer controls offer.
 */
export function analyzeTabTimeControls(
  widgets: WidgetResponse[] | undefined,
  guardrailsByDataset?: Record<string, DateGuardrails>,
): TabTimeControlsAnalysis {
  const { trend: trendMembers, pointInTime: pointInTimeMembers } =
    buildFamilyMembers(widgets, guardrailsByDataset);

  const trend = buildFamily("trend", trendMembers);
  const pointInTime = buildFamily("pointInTime", pointInTimeMembers);

  return {
    trend,
    pointInTime,
    showTrigger: Boolean(trend || pointInTime),
  };
}

// Selectable-option builders + active-state derivation live in a sibling file
// (keeps this engine file focused); re-exported so consumers import from here.
export * from "./tab-time-controls-options";
// Editor-facing alignment check (Module 2) — sibling file, re-exported here.
export * from "./tab-time-alignment";
